import { groupBy } from 'lodash-es'
import { makeAutoObservable } from 'mobx'

import CheckableMap from '@/models/CheckableMap'

import type { Student } from './Student'

type K = Student['id']
type StudentsByFlatGrade = Partial<Record<Student['schoolGrade']['flatGrade'], Student[]>>

/***
 * @see Student
 ***/
export class StudentColl {
  private studentMap: CheckableMap<K, Student>

  constructor(students?: Student[]) {
    makeAutoObservable(this)

    if (students) this.studentMap = CheckableMap.fromArr(students, 'id')
    else this.studentMap = CheckableMap.fromArr([] as Student[], 'id')
  }

  get toArr() {
    return this.studentMap.toArr
  }

  get size() {
    return this.studentMap.size
  }

  get checkSize() {
    return this.studentMap.checkSize
  }

  get checkedStudents() {
    return this.studentMap.toCheckedValues
  }

  get checkedIds() {
    return this.studentMap.toCheckedKeys
  }

  get students() {
    return [...this.studentMap.values()]
  }

  get studentIds() {
    return [...this.studentMap.keys()]
  }

  // 체크된 학생이 전부 퇴원생
  get isAllCheckStudentsInactive() {
    return this.studentMap.toCheckedValues.every((student) => student.status === 'INACTIVE')
  }

  get isNotEmpty() {
    return this.studentMap.isNotEmpty
  }

  get isEmpty() {
    return this.studentMap.isEmpty
  }

  get isPartChecked() {
    return this.studentMap.isPartChecked
  }

  // 한 페이지의 학생들이 모두 체크되었는지에 대한 값
  get isFullyChecked() {
    return this.studentMap.isFullyChecked
  }

  byFlatGrade(): StudentsByFlatGrade {
    return groupBy(this.students, (student) => student.schoolGrade.flatGrade)
  }

  deleteStudent(studentId: K) {
    return this.studentMap.delete(studentId)
  }

  deleteStudents(studentIds: K[]) {
    return this.studentMap.deletes(studentIds)
  }

  setStudent(student: Student) {
    this.studentMap.set(student.id, student)
  }

  toggleCheck(key: K) {
    return this.studentMap.toggleCheck(key)
  }

  isChecked(key: K) {
    return this.studentMap.isChecked(key)
  }

  toggleAllCheck() {
    return this.studentMap.toggleAllCheck()
  }

  clearAllCheck() {
    return this.studentMap.clearAllCheck()
  }

  values() {
    return this.studentMap.values()
  }

  get(key: K) {
    return this.studentMap.get(key)
  }

  delete(studentId: K) {
    return this.studentMap.delete(studentId)
  }

  deletes(studentIds: K[]) {
    return this.studentMap.deletes(studentIds)
  }

  set(student: Student) {
    this.studentMap.set(student.id, student)
  }

  add(student: Student) {
    this.studentMap.set(student['id'], student)
  }

  adds(studentColl: StudentColl) {
    this.studentMap.adds(studentColl.studentMap.entries())
  }

  clear() {
    this.studentMap.clear()
  }
}
